import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Spinner } from "@air/primitive-spinner";
import usePrevious from "core/src/hooks/usePrevious";
import { PixelsPerInch, setLayerDimensions } from "pdfjs-dist";
import { memo, useCallback, useLayoutEffect, useRef, useState } from "react";
import { getMaxCanvasPixels } from "../utils/constants";
import { setPDFPageCanvasSize } from "../utils/setPDFPageCanvasSize";
import { PDFPageAnnotationLayer } from "./PDFPageAnnotationLayer";
import { PDFPageTextLayer } from "./PDFPageTextLayer/PDFPageTextLayer";
const addZoomingAttribute = (element)=>{
    if (!element.hasAttribute("zooming")) {
        element.setAttribute("zooming", "true");
        element.style.width = "";
        element.style.height = "";
    }
};
const SCALE_DELAY_MS = 400;
export const PDFViewerPage = /*#__PURE__*/ memo((param)=>{
    let { scale, pageNumber, pdfPageCacheRef, linkServiceRef, cachedHeight, renderPageLayers } = param;
    const canvasRef = useRef();
    const zoomLayerRef = useRef();
    const containerRef = useRef(null);
    const canvasWrapperRef = useRef(null);
    const [pdfPage, setPdfPage] = useState();
    const [pdfPageViewport, setPdfPageViewport] = useState();
    const [isLoaded, setIsLoaded] = useState(false);
    const renderTaskRef = useRef();
    const scaleTimeoutRef = useRef();
    const isMounted = useRef(false);
    const scaleRef = useRef();
    scaleRef.current = scale;
    const prevScale = usePrevious(scale);
    useLayoutEffect(()=>{
        isMounted.current = true;
        return ()=>{
            isMounted.current = false;
        };
    }, []);
    const clearZoomLayer = useCallback(()=>{
        var _zoomLayerRef_current;
        if (!zoomLayerRef.current) return;
        zoomLayerRef.current.width = 0;
        zoomLayerRef.current.height = 0;
        (_zoomLayerRef_current = zoomLayerRef.current) === null || _zoomLayerRef_current === void 0 ? void 0 : _zoomLayerRef_current.remove();
        zoomLayerRef.current = undefined;
    }, []);
    const cancelRenderTask = useCallback(async ()=>{
        var _renderTaskRef_current;
        const currentTask = renderTaskRef.current;
        (_renderTaskRef_current = renderTaskRef.current) === null || _renderTaskRef_current === void 0 ? void 0 : _renderTaskRef_current.cancel();
        renderTaskRef.current = undefined;
        // renderTaskRef is assigned to another promise, but we still have to catch errors from cancelling the previous one
        currentTask === null || currentTask === void 0 ? void 0 : currentTask.promise.catch((error)=>{
            if (error.name === "RenderingCancelledException") return;
            console.error("renderTask.promise error", error);
        });
        window.clearTimeout(scaleTimeoutRef.current);
    }, []);
    const loadPage = useCallback(async function() {
        let renderDelay = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 0;
        var _containerRef_current;
        if (!canvasWrapperRef.current || !containerRef.current || !pdfPageCacheRef.current || !scaleRef.current) return;
        if (!zoomLayerRef.current) {
            zoomLayerRef.current = canvasRef.current;
        }
        cancelRenderTask();
        canvasRef.current = document.createElement("canvas");
        canvasRef.current.setAttribute("role", "presentation");
        canvasRef.current.hidden = true;
        if (zoomLayerRef.current) {
            addZoomingAttribute(zoomLayerRef.current);
            zoomLayerRef.current.style.position = "absolute";
        }
        const ctx = canvasRef.current.getContext("2d", {
            alpha: false,
            willReadFrequently: true
        });
        if (!ctx) return;
        const pdfPage = await pdfPageCacheRef.current.getPage(pageNumber);
        if (!pdfPage) {
            return;
        }
        if (!isMounted.current) {
            return;
        }
        const scaleToSet = scaleRef.current * PixelsPerInch.PDF_TO_CSS_UNITS;
        const viewport = pdfPage.getViewport({
            scale: scaleToSet
        });
        setLayerDimensions(containerRef.current, viewport, true, false);
        (_containerRef_current = containerRef.current) === null || _containerRef_current === void 0 ? void 0 : _containerRef_current.style.setProperty("--scale-factor", scaleToSet.toString());
        window.clearTimeout(scaleTimeoutRef.current);
        scaleTimeoutRef.current = window.setTimeout(()=>{
            var _renderTaskRef_current;
            if (!canvasRef.current || !isMounted.current) return;
            const { transform } = setPDFPageCanvasSize({
                maxCanvasPixels: getMaxCanvasPixels(),
                canvas: canvasRef.current,
                width: viewport.width,
                height: viewport.height
            });
            renderTaskRef.current = pdfPage.render({
                canvasContext: ctx,
                transform,
                viewport
            });
            (_renderTaskRef_current = renderTaskRef.current) === null || _renderTaskRef_current === void 0 ? void 0 : _renderTaskRef_current.promise.then(()=>{
                if (!!canvasWrapperRef.current && !!canvasRef.current && isMounted.current) {
                    canvasWrapperRef.current.appendChild(canvasRef.current);
                    canvasRef.current.hidden = false;
                    setIsLoaded(true);
                    setPdfPage(pdfPage);
                    setPdfPageViewport(viewport);
                }
                clearZoomLayer();
            }, (error)=>{
                if (error.name === "RenderingCancelledException") return;
                console.error("renderTask.promise error", error);
                clearZoomLayer();
            });
        }, renderDelay);
    }, [
        cancelRenderTask,
        clearZoomLayer,
        pageNumber,
        pdfPageCacheRef
    ]);
    useLayoutEffect(()=>{
        loadPage();
        return ()=>{
            cancelRenderTask();
            clearZoomLayer();
            if (canvasRef.current) {
                canvasRef.current.width = 0;
                canvasRef.current.height = 0;
            }
        };
    }, [
        cancelRenderTask,
        clearZoomLayer,
        loadPage,
        pageNumber,
        pdfPageCacheRef
    ]);
    const loadPageRef = useRef(loadPage);
    loadPageRef.current = loadPage;
    useLayoutEffect(()=>{
        if (!prevScale || scale === prevScale) return;
        loadPageRef.current(SCALE_DELAY_MS);
    }, [
        prevScale,
        scale
    ]);
    var _pdfPageViewport_height;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "PDF_VIEWER_PAGE",
        "data-page-number": pageNumber,
        role: "region",
        ref: containerRef,
        className: "pdfPage relative mx-auto",
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: canvasWrapperRef,
                className: "pdfCanvasWrapper",
                style: {
                    height: (_pdfPageViewport_height = pdfPageViewport === null || pdfPageViewport === void 0 ? void 0 : pdfPageViewport.height) !== null && _pdfPageViewport_height !== void 0 ? _pdfPageViewport_height : cachedHeight
                }
            }),
            !!pdfPage && !!pdfPageViewport && /*#__PURE__*/ _jsx(PDFPageTextLayer, {
                pageNumber: pageNumber,
                pdfPage: pdfPage,
                pageViewport: pdfPageViewport
            }),
            !!pdfPage && !!pdfPageViewport && linkServiceRef.current && /*#__PURE__*/ _jsx(PDFPageAnnotationLayer, {
                linkService: linkServiceRef.current,
                pdfPage: pdfPage,
                pageViewport: pdfPageViewport
            }),
            renderPageLayers === null || renderPageLayers === void 0 ? void 0 : renderPageLayers({
                pageNumber,
                containerRef: canvasWrapperRef
            }),
            !isLoaded && /*#__PURE__*/ _jsx("div", {
                className: "absolute inset-0 flex items-center justify-center bg-grey-12",
                children: /*#__PURE__*/ _jsx(Spinner, {
                    className: "size-8 text-teal-9"
                })
            })
        ]
    });
});
PDFViewerPage.displayName = "PDFViewerPage";
